'use client';

import { Text, TextInput } from '@mantine/core';
import type { TextInputProps } from '@mantine/core';
import Phone from '@material-design-icons/svg/filled/phone.svg';
import { useEffect, useRef, useState } from 'react';

/**
 * A validated phone input component that formats and validates US phone numbers,
 * automatically adding the +1 country code.
 */
const ValidatedPhoneInput = ({
  value,
  onChange,
  onFocus = () => {},
  label,
  placeholder = '(555) 123-4567',
  error,
  required = false,
  autoFocus = false,
  ...props
}: { onChange: (value: string) => void } & Omit<
  TextInputProps,
  'onChange'
>) => {
  const [displayValue, setDisplayValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus input on mount
  useEffect(() => {
    if (autoFocus && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 500);
    }
  }, [autoFocus]);

  // Initialize formatted value from prop
  useEffect(() => {
    const stringValue = (value as string)?.toString() ?? '';
    if (stringValue && stringValue != '') {
      // Remove the +1 prefix if it exists before formatting
      const normalizedValue =
        (stringValue.startsWith('+1') ? stringValue.substring(2) : value) ?? '';

      setDisplayValue(formatPhoneNumber(normalizedValue as string));
    }
  }, [value]);

  /**
   * Format phone number for display: (XXX) XXX-XXXX
   */
  const formatPhoneNumber = (number: string) => {
    // Remove all non-numeric characters
    const cleaned = number.replace(/\D/g, '');

    // Format the digits
    let formatted = '';
    if (cleaned?.length > 0) {
      formatted += '(';
      formatted += cleaned.substring(0, Math.min(3, cleaned?.length));

      if (cleaned?.length > 3) {
        formatted += ') ';
        formatted += cleaned.substring(3, Math.min(6, cleaned?.length));

        if (cleaned?.length > 6) {
          formatted += '-';
          formatted += cleaned.substring(6, Math.min(10, cleaned?.length));
        }
      }
    }

    return formatted;
  };

  /**
   * Convert formatted phone number to E.164 format (+1XXXXXXXXXX)
   */
  const toE164Format = (formattedNumber: string) => {
    // Remove all non-numeric characters
    const digits = formattedNumber.replace(/\D/g, '');

    // Make sure it starts with country code
    return `+1${digits}`;
  };

  /**
   * Handle input change and format the number
   */
  const handleChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const { value } = e.currentTarget;
    // Store the raw input value (limited to 10 digits)
    const limitedValue = value.replace(/\D/g, '').substring(0, 10);

    // Format the value for display
    const formattedNumber = formatPhoneNumber(limitedValue);
    setDisplayValue(formattedNumber);

    // Call the parent's onChange with the E.164 formatted number
    if (onChange) {
      const e164Number = toE164Format(limitedValue);
      onChange(e164Number);
    }
  };

  /**
   * Validate the phone number
   */
  const validatePhoneNumber = (value: string) => {
    // Remove all non-numeric characters to check length
    const digitsOnly = value.replace(/\D/g, '');

    // For US numbers, should be 10 digits
    return digitsOnly?.length === 10;
  };

  // Determine error state
  const hasError =
    error || (displayValue && !validatePhoneNumber(displayValue));
  const errorMessage =
    error ||
    (hasError && displayValue?.length > 0
      ? 'Please enter a valid 10-digit US phone number'
      : null);

  return (
    <TextInput
      ref={inputRef}
      type="tel"
      label={label}
      placeholder={placeholder}
      value={displayValue}
      onChange={handleChange}
      onFocus={onFocus}
      error={errorMessage}
      required={required}
      leftSection={<Text>+1</Text>}
      leftSectionPointerEvents="none"
      rightSection={<Phone />}
      rightSectionPointerEvents="none"
      autoFocus={autoFocus}
      {...props}
    />
  );
};

export { ValidatedPhoneInput };
